import { buildBlockRecord } from '@datocms/cma-client-browser';
import {
  VIDEO_TEMPLATE_GLOBAL_PRESET_ID_QUERY,
  VIDEO_TEMPLATES_QUERY,
  VIDEO_TEMPLATES_WITHOUT_GLOBAL_QUERY,
} from '@src/gql/video-templates-gql';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { fetchAllGraphqlPages } from '@src/utility/paginateGraphql';
import transformKeysToSnakeCase from '@src/utility/transformKeysToSnakeCase';

import type { Client } from '@datocms/cma-client-browser';
import type ApiClient from '@src/apiClient/ApiClient';
import type { VideoTemplate } from '@src/components/sidepanel/VideoTemplateSettings/types';
import type { AspectRatio } from '@src/types.ts/video';
import type { GraphQLClient } from 'graphql-request';

type BlockModelIds = {
  configModelId: string;
  logoConfigModelId: string;
  captionsConfigModelId: string;
  namecardConfigModelId: string | null;
  introCardConfigModelId: string | null;
  endCardConfigModelId: string | null;
  /** PLAT-11034 — null on a template predating the hook block (PLAT-11033). */
  hookConfigModelId: string | null;
};

/** Sub-config model-ID keys that are required (typed as `string`, not `string | null`). */
type RequiredModelIdKey = {
  [K in keyof BlockModelIds]: BlockModelIds[K] extends string ? K : never;
}[keyof BlockModelIds];

const REQUIRED_MODEL_ID_KEYS: Record<RequiredModelIdKey, true> = {
  configModelId: true,
  logoConfigModelId: true,
  captionsConfigModelId: true,
};

/** Extracts the model ID from a CMA record's `item_type` field. */
function getModelId(record: Record<string, unknown>): string {
  return (record.item_type as { id: string }).id;
}

/** Fields that must be stripped from CMA records before passing to buildBlockRecord. */
const CMA_META_KEYS = new Set(['id', 'type', 'item_type', 'meta', 'creator']);

/** Strips CMA meta fields that cannot be passed back to buildBlockRecord. */
function stripCmaMetaFields(
  record: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(record).filter(([key]) => !CMA_META_KEYS.has(key)),
  );
}

/**
 * Wraps buildBlockRecord to guarantee the result always contains an
 * `attributes` key. DatoCMS rejects inline block records where `attributes`
 * is absent, but the upstream helper omits it when there are no attribute
 * fields.
 */
function buildBlockRecordSafe(
  fields: Record<string, unknown>,
): ReturnType<typeof buildBlockRecord> {
  const record = buildBlockRecord(fields);
  if (!('attributes' in record)) {
    (record as Record<string, unknown>).attributes = {};
  }
  return record;
}

export class VideoTemplateRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;
  private logger = LogManager.getLogger('repository:VideoTemplateRepository');

  // In-memory cache with TTL to avoid refetching on modal re-open
  private cache = new Map<
    string,
    { data: VideoTemplate[]; timestamp: number }
  >();
  private CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
  }

  async getVideoTemplates(
    whitelistIds: string[] = [],
    options: { excludeGlobalPresets?: boolean } = {},
  ): Promise<VideoTemplate[]> {
    const { excludeGlobalPresets = false } = options;
    const cacheKey = `${[...whitelistIds].sort().join(',')}|exclude:${excludeGlobalPresets}`;
    const cached = this.cache.get(cacheKey);

    // Return cached data if still valid
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL_MS) {
      this.logger.debug('Returning cached video templates', { cacheKey });
      return cached.data;
    }

    // Fetch fresh data
    const query = excludeGlobalPresets
      ? VIDEO_TEMPLATES_WITHOUT_GLOBAL_QUERY
      : VIDEO_TEMPLATES_QUERY;
    const templates = await fetchAllGraphqlPages<VideoTemplate>(
      this.gqlClient,
      query,
      'allVideoTemplates',
      { ids: whitelistIds },
    );

    // Cache the result
    this.cache.set(cacheKey, { data: templates, timestamp: Date.now() });
    this.logger.debug('Cached video templates', {
      cacheKey,
      count: templates.length,
    });

    return templates;
  }

  /**
   * Fetches any single global preset template ID. Used as a reference to
   * discover block model IDs when a customer has no templates of their own.
   */
  async getReferenceGlobalPresetId(): Promise<string | null> {
    try {
      const response = await this.gqlClient.request<{
        allVideoTemplates: { id: string }[];
      }>(VIDEO_TEMPLATE_GLOBAL_PRESET_ID_QUERY);
      return response.allVideoTemplates[0]?.id ?? null;
    } catch (err) {
      this.logger.error('Failed to fetch reference global preset ID', {
        error: extractErrorInfo(err),
      });
      return null;
    }
  }

  /**
   * Creates a new empty VideoTemplate in DatoCMS.
   * Discovers block model IDs from an existing template so the new template
   * has a valid common_configuration and can later be updated via
   * updateConfigurations.
   *
   * @returns The ID of the newly created template.
   */
  async createVideoTemplate(
    label: string,
    metadata: {
      parentTemplateId: string;
      parentShowcaseId: string;
      createdByUserId: string;
      showcaseName: string;
    },
  ): Promise<string> {
    const client = this.dClient as Client;

    // Discover block model IDs from an existing template
    const existingItem = await client.items.find(metadata.parentTemplateId);

    const blockModelIds = await this.discoverBlockModelIds(
      client,
      existingItem,
    );

    // Find the video_template item type
    const itemType = await client.itemTypes.find('video_template');

    // Build common_configuration with empty sub-blocks
    const commonConfig = buildBlockRecord({
      item_type: { type: 'item_type', id: blockModelIds.configModelId },
      logo_configuration: buildBlockRecord({
        item_type: {
          type: 'item_type',
          id: blockModelIds.logoConfigModelId,
        },
        enabled: false,
        logo: null,
        opacity: null,
        placement_x: null,
        placement_y: null,
        width: null,
        height: null,
        fit: null,
      }),
      captions_configuration: buildBlockRecord({
        item_type: {
          type: 'item_type',
          id: blockModelIds.captionsConfigModelId,
        },
        enabled: false,
        captions_config: null,
        vertical_position: null,
        horizontal_position: null,
        horizontal_alignment: null,
        font_size: null,
      }),
      ...(blockModelIds.namecardConfigModelId && {
        namecard_configuration: buildBlockRecord({
          item_type: {
            type: 'item_type',
            id: blockModelIds.namecardConfigModelId,
          },
          enabled: false,
          composition_template: null,
          placement_override: null,
        }),
      }),
      ...(blockModelIds.introCardConfigModelId && {
        intro_card_configuration: buildBlockRecord({
          item_type: {
            type: 'item_type',
            id: blockModelIds.introCardConfigModelId,
          },
          enabled: false,
          composition_template: null,
        }),
      }),
      ...(blockModelIds.endCardConfigModelId && {
        end_card_configuration: buildBlockRecord({
          item_type: {
            type: 'item_type',
            id: blockModelIds.endCardConfigModelId,
          },
          enabled: false,
          composition_template: null,
        }),
      }),
      ...(blockModelIds.hookConfigModelId && {
        hook_configuration: buildBlockRecord({
          item_type: {
            type: 'item_type',
            id: blockModelIds.hookConfigModelId,
          },
          enabled: false,
          composition_template: null,
          placement_override: null,
          duration: null,
        }),
      }),
    });

    const item = await client.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      label,
      internal_name: `Customer | ${label} | ${metadata.showcaseName}`,
      is_global_preset: false,
      is_deleted: false,
      common_configuration: commonConfig,
      created_by_user: metadata.createdByUserId,
      created_by_showcase: metadata.parentShowcaseId,
    });
    await client.items.publish(item.id);
    this.clearCache();

    this.logger.info('Created new video template', {
      templateId: item.id,
      label,
    });

    return item.id;
  }

  /**
   * Renames an existing VideoTemplate.
   *
   * `internalName` is optional because only customer templates follow the
   * `Customer | <label> | <showcase>` shape createVideoTemplate writes; global
   * presets keep whatever internal name they were authored with.
   */
  async renameVideoTemplate(
    templateId: string,
    label: string,
    internalName?: string,
  ): Promise<void> {
    const client = this.dClient as Client;

    await client.items.update(templateId, {
      label,
      ...(internalName ? { internal_name: internalName } : {}),
    });
    await client.items.publish(templateId);
    this.clearCache();

    this.logger.info('Renamed video template', { templateId, label });
  }

  /**
   * Adds a video template to a customer type's videoTemplates list.
   *
   * The current list is read from the server rather than taken from the caller:
   * the update writes the whole relation, so a list captured when the story
   * loaded drops everything linked since — including a template this session
   * created moments ago. Re-linking an already-linked template is a no-op.
   *
   * @returns The customer type's template ids after the link.
   */
  async addTemplateToCustomerType(
    customerTypeId: string,
    templateId: string,
  ): Promise<string[]> {
    const client = this.dClient as Client;

    const customerType = await client.items.find(customerTypeId);
    const currentIds = (customerType.video_templates as string[] | null) ?? [];
    if (currentIds.includes(templateId)) {
      this.logger.info('Template already linked to customer type', {
        customerTypeId,
        templateId,
      });
      return currentIds;
    }

    const allIds = [...currentIds, templateId];

    await client.items.update(customerTypeId, {
      video_templates: allIds,
    });

    await client.items.publish(customerTypeId);

    this.logger.info('Added template to customer type', {
      customerTypeId,
      templateId,
    });

    return allIds;
  }

  /**
   * Creates a deep copy of an existing VideoTemplate with all configurations.
   * `common_configuration` is the preset baseline and is copied verbatim from
   * the source. The customer-edit `overrideConfigs` are applied to the slot
   * matching `currentAspectRatio`; the other two override slots are copied
   * as-is when present.
   *
   * @returns The ID of the newly created template.
   */
  async duplicateVideoTemplate(
    label: string,
    currentAspectRatio: AspectRatio,
    overrideConfigs: {
      logoConfiguration?: Record<string, unknown>;
      captionsConfiguration?: Record<string, unknown>;
      namecardConfiguration?: Record<string, unknown>;
      introCardConfiguration?: Record<string, unknown>;
      endCardConfiguration?: Record<string, unknown>;
      hookConfiguration?: Record<string, unknown>;
    },
    metadata: {
      parentTemplateId: string;
      parentShowcaseId: string;
      createdByUserId: string;
      showcaseName: string;
    },
  ): Promise<string> {
    const client = this.dClient as Client;
    const rawItem = await client.items.find(metadata.parentTemplateId);
    const blockModelIds = await this.discoverBlockModelIds(client, rawItem);
    const itemType = await client.itemTypes.find('video_template');

    const commonConfigBlockId = rawItem.common_configuration as string | null;
    const commonConfig = commonConfigBlockId
      ? await this.copyConfigBlockFromCma(
          client,
          commonConfigBlockId,
          blockModelIds,
        )
      : this.buildEmptyConfigBlock(blockModelIds);

    const overrideCmaKey = getOverrideCmaFieldKey(currentAspectRatio);
    const overrideSlots: Record<string, unknown> = {};

    for (const key of OVERRIDE_CMA_KEYS) {
      const blockId = rawItem[key] as string | null;

      if (key === overrideCmaKey) {
        overrideSlots[key] = await this.buildConfigBlockWithOverrides(
          client,
          blockId,
          blockModelIds,
          overrideConfigs,
        );
      } else if (blockId) {
        overrideSlots[key] = await this.copyConfigBlockFromCma(
          client,
          blockId,
          blockModelIds,
        );
      }
    }

    const item = await client.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      label,
      internal_name: `Customer | ${label} | ${metadata.showcaseName}`,
      is_global_preset: false,
      is_deleted: false,
      common_configuration: commonConfig,
      ...overrideSlots,
      parent_template: metadata.parentTemplateId,
      created_by_showcase: metadata.parentShowcaseId,
      created_by_user: metadata.createdByUserId,
    });

    await client.items.publish(item.id);
    this.clearCache();

    this.logger.info('Duplicated video template', {
      parentTemplateId: metadata.parentTemplateId,
      newTemplateId: item.id,
      label,
      currentAspectRatio,
      metadata,
    });

    return item.id;
  }

  /**
   * Updates one or more configuration blocks within a template config field.
   * The `targetCmaField` determines which top-level field is written:
   * - `'override16x9'` / `'override9x16'` / `'override1x1'` for AR-specific overrides
   * - `'common_configuration'` for the preset baseline (admin tooling only;
   *   customer edits always target an override)
   *
   * Always updates the top-level template item with inline buildBlockRecord
   * structures, because the API proxy does not support direct block record updates.
   * All provided configs are written in a single API call so other configs are not wiped.
   */
  async updateConfigurations(
    templateId: string,
    targetCmaField: string,
    configs: {
      logoConfiguration?: Record<string, unknown>;
      captionsConfiguration?: Record<string, unknown>;
      namecardConfiguration?: Record<string, unknown>;
      introCardConfiguration?: Record<string, unknown>;
      endCardConfiguration?: Record<string, unknown>;
      hookConfiguration?: Record<string, unknown>;
    },
  ): Promise<void> {
    const client = this.dClient as Client;

    const rawItem = await client.items.find(templateId);
    const blockModelIds = await this.discoverBlockModelIds(client, rawItem);

    // Read existing block to preserve configs not being updated
    const existingBlockId = rawItem[targetCmaField] as string | null;
    let existingBlock: Record<string, unknown> | null = null;
    if (existingBlockId) {
      existingBlock = await client.items.find(existingBlockId);
    }

    const overrideFields: Record<string, unknown> = {
      item_type: { type: 'item_type', id: blockModelIds.configModelId },
    };

    // Preserve existing sub-blocks for configs NOT being updated.
    // Sub-blocks are stored as IDs, but DatoCMS expects full block record
    // objects when rebuilding the parent inline block. Fetch each preserved
    // sub-block and re-wrap it with buildBlockRecord.
    const configFieldMap = {
      logoConfiguration: 'logo_configuration',
      captionsConfiguration: 'captions_configuration',
      namecardConfiguration: 'namecard_configuration',
      introCardConfiguration: 'intro_card_configuration',
      endCardConfiguration: 'end_card_configuration',
      hookConfiguration: 'hook_configuration',
    } as const;

    for (const [configKey, cmaField] of Object.entries(configFieldMap)) {
      if (
        !configs[configKey as keyof typeof configs] &&
        existingBlock?.[cmaField]
      ) {
        const subBlockId = existingBlock[cmaField] as string;
        const subBlock = await client.items.find(subBlockId);
        const subBlockModelId = getModelId(subBlock);
        overrideFields[cmaField] = buildBlockRecord({
          item_type: { type: 'item_type', id: subBlockModelId },
          ...stripCmaMetaFields(subBlock),
        });
      }
    }

    if (configs.logoConfiguration) {
      const logoCma = formatLogoConfigForCma(configs.logoConfiguration);
      overrideFields.logo_configuration = buildBlockRecord({
        item_type: { type: 'item_type', id: blockModelIds.logoConfigModelId },
        ...logoCma,
      });
    }

    if (configs.captionsConfiguration) {
      const captionsCma = formatCaptionsConfigForCma(
        configs.captionsConfiguration,
      );
      overrideFields.captions_configuration = buildBlockRecord({
        item_type: {
          type: 'item_type',
          id: blockModelIds.captionsConfigModelId,
        },
        ...captionsCma,
      });
    }

    if (configs.namecardConfiguration) {
      if (!blockModelIds.namecardConfigModelId) {
        throw new Error(
          'Template has no namecard_configuration — cannot update namecard placement.',
        );
      }
      const namecardCma = formatNamecardConfigForCma(
        configs.namecardConfiguration,
      );
      overrideFields.namecard_configuration = buildBlockRecord({
        item_type: {
          type: 'item_type',
          id: blockModelIds.namecardConfigModelId,
        },
        ...namecardCma,
      });
    }

    if (configs.introCardConfiguration) {
      if (!blockModelIds.introCardConfigModelId) {
        throw new Error(
          'Template has no intro_card_configuration — cannot update intro card.',
        );
      }
      const introCma = formatCardConfigForCma(configs.introCardConfiguration);
      overrideFields.intro_card_configuration = buildBlockRecord({
        item_type: {
          type: 'item_type',
          id: blockModelIds.introCardConfigModelId,
        },
        ...introCma,
      });
    }

    if (configs.endCardConfiguration) {
      if (!blockModelIds.endCardConfigModelId) {
        throw new Error(
          'Template has no end_card_configuration — cannot update end card.',
        );
      }
      const endCma = formatCardConfigForCma(configs.endCardConfiguration);
      overrideFields.end_card_configuration = buildBlockRecord({
        item_type: {
          type: 'item_type',
          id: blockModelIds.endCardConfigModelId,
        },
        ...endCma,
      });
    }

    if (configs.hookConfiguration) {
      if (!blockModelIds.hookConfigModelId) {
        throw new Error(
          'Template has no hook_configuration — cannot update the hook.',
        );
      }
      const hookCma = formatHookConfigForCma(configs.hookConfiguration);
      overrideFields.hook_configuration = buildBlockRecord({
        item_type: {
          type: 'item_type',
          id: blockModelIds.hookConfigModelId,
        },
        ...hookCma,
      });
    }

    const updatePayload: Record<string, unknown> = {
      [targetCmaField]: buildBlockRecord(overrideFields),
    };

    await client.items.update(templateId, updatePayload);

    this.clearCache();
    this.logger.info('Updated configurations for template', {
      templateId,
      targetCmaField,
    });
  }

  /**
   * Copies a full configuration block (and its sub-blocks) from CMA by ID.
   * Returns a new inline buildBlockRecord suitable for creating a new template item.
   */
  private async copyConfigBlockFromCma(
    client: Client,
    configBlockId: string,
    blockModelIds: BlockModelIds,
  ) {
    const configBlock = await client.items.find(configBlockId);
    const fields: Record<string, unknown> = {
      item_type: { type: 'item_type', id: blockModelIds.configModelId },
    };

    for (const { cmaField, modelIdKey } of SUB_CONFIG_FIELD_MAP) {
      const subBlockId = configBlock[cmaField] as string | null;
      const modelId = blockModelIds[modelIdKey];
      if (subBlockId && modelId) {
        const subBlock = await client.items.find(subBlockId);
        fields[cmaField] = buildBlockRecordSafe({
          item_type: { type: 'item_type', id: modelId },
          ...stripCmaMetaFields(subBlock),
        });
      }
    }

    return buildBlockRecordSafe(fields);
  }

  /**
   * Builds a config block from an existing CMA block (if any), with specific
   * sub-configs overridden by the provided values. Sub-configs not in
   * `overrides` are copied from the existing block.
   */
  private async buildConfigBlockWithOverrides(
    client: Client,
    existingBlockId: string | null,
    blockModelIds: BlockModelIds,
    overrides: {
      logoConfiguration?: Record<string, unknown>;
      captionsConfiguration?: Record<string, unknown>;
      namecardConfiguration?: Record<string, unknown>;
      introCardConfiguration?: Record<string, unknown>;
      endCardConfiguration?: Record<string, unknown>;
      hookConfiguration?: Record<string, unknown>;
    },
  ) {
    const fields: Record<string, unknown> = {
      item_type: { type: 'item_type', id: blockModelIds.configModelId },
    };

    // Map override keys to CMA field names and format functions
    const overrideMap: Array<{
      overrideKey: keyof typeof overrides;
      cmaField: string;
      modelIdKey: keyof BlockModelIds;
      format: (v: Record<string, unknown>) => Record<string, unknown>;
    }> = [
      {
        overrideKey: 'logoConfiguration',
        cmaField: 'logo_configuration',
        modelIdKey: 'logoConfigModelId',
        format: formatLogoConfigForCma,
      },
      {
        overrideKey: 'captionsConfiguration',
        cmaField: 'captions_configuration',
        modelIdKey: 'captionsConfigModelId',
        format: formatCaptionsConfigForCma,
      },
      {
        overrideKey: 'namecardConfiguration',
        cmaField: 'namecard_configuration',
        modelIdKey: 'namecardConfigModelId',
        format: formatNamecardConfigForCma,
      },
      {
        overrideKey: 'introCardConfiguration',
        cmaField: 'intro_card_configuration',
        modelIdKey: 'introCardConfigModelId',
        format: formatCardConfigForCma,
      },
      {
        overrideKey: 'endCardConfiguration',
        cmaField: 'end_card_configuration',
        modelIdKey: 'endCardConfigModelId',
        format: formatCardConfigForCma,
      },
      {
        overrideKey: 'hookConfiguration',
        cmaField: 'hook_configuration',
        modelIdKey: 'hookConfigModelId',
        format: formatHookConfigForCma,
      },
    ];

    // Read existing block to preserve sub-configs not being overridden
    let existingBlock: Record<string, unknown> | null = null;
    if (existingBlockId) {
      existingBlock = await client.items.find(existingBlockId);
    }

    for (const { overrideKey, cmaField, modelIdKey, format } of overrideMap) {
      const modelId = blockModelIds[modelIdKey];
      if (!modelId) continue;

      const overrideValue = overrides[overrideKey];
      if (overrideValue) {
        // Apply the override with CMA formatting
        const formatted = format(overrideValue);
        fields[cmaField] = buildBlockRecordSafe({
          item_type: { type: 'item_type', id: modelId },
          ...formatted,
        });
      } else if (existingBlock?.[cmaField]) {
        // Copy existing sub-block as-is
        const subBlockId = existingBlock[cmaField] as string;
        const subBlock = await client.items.find(subBlockId);
        fields[cmaField] = buildBlockRecordSafe({
          item_type: { type: 'item_type', id: modelId },
          ...stripCmaMetaFields(subBlock),
        });
      }
    }

    return buildBlockRecordSafe(fields);
  }

  /**
   * Builds an empty config block with all sub-configs disabled.
   */
  private buildEmptyConfigBlock(blockModelIds: BlockModelIds) {
    return buildBlockRecord({
      item_type: { type: 'item_type', id: blockModelIds.configModelId },
      logo_configuration: buildBlockRecord({
        item_type: { type: 'item_type', id: blockModelIds.logoConfigModelId },
        enabled: false,
        logo: null,
        opacity: null,
        placement_x: null,
        placement_y: null,
        width: null,
        height: null,
        fit: null,
      }),
      captions_configuration: buildBlockRecord({
        item_type: {
          type: 'item_type',
          id: blockModelIds.captionsConfigModelId,
        },
        enabled: false,
        captions_config: null,
        vertical_position: null,
        horizontal_position: null,
        horizontal_alignment: null,
        font_size: null,
      }),
      ...(blockModelIds.namecardConfigModelId && {
        namecard_configuration: buildBlockRecord({
          item_type: {
            type: 'item_type',
            id: blockModelIds.namecardConfigModelId,
          },
          enabled: false,
          composition_template: null,
          placement_override: null,
        }),
      }),
      ...(blockModelIds.introCardConfigModelId && {
        intro_card_configuration: buildBlockRecord({
          item_type: {
            type: 'item_type',
            id: blockModelIds.introCardConfigModelId,
          },
          enabled: false,
          composition_template: null,
        }),
      }),
      ...(blockModelIds.endCardConfigModelId && {
        end_card_configuration: buildBlockRecord({
          item_type: {
            type: 'item_type',
            id: blockModelIds.endCardConfigModelId,
          },
          enabled: false,
          composition_template: null,
        }),
      }),
      ...(blockModelIds.hookConfigModelId && {
        hook_configuration: buildBlockRecord({
          item_type: {
            type: 'item_type',
            id: blockModelIds.hookConfigModelId,
          },
          enabled: false,
          composition_template: null,
          placement_override: null,
          duration: null,
        }),
      }),
    });
  }

  /**
   * Discovers block model IDs by inspecting any populated configuration block
   * on the template — `common_configuration` if present, otherwise any
   * override (`override16x9`, `override9x16`, `override1x1`). All configuration
   * blocks share the same sub-block model IDs, so any non-null one is a valid
   * source. Sub-block IDs are merged across candidates so that, e.g., a
   * namecard model ID present only in an override is still discovered.
   */
  private async discoverBlockModelIds(
    client: Client,
    rawItem: Record<string, unknown>,
  ): Promise<BlockModelIds> {
    const candidateFields = ['common_configuration', ...OVERRIDE_CMA_KEYS];
    const candidateBlockIds = candidateFields
      .map(f => rawItem[f] as string | null)
      .filter((id): id is string => !!id);

    if (candidateBlockIds.length === 0) {
      throw new Error(
        'Template has no configuration blocks — cannot discover block model IDs.',
      );
    }

    const resolveSubBlockModelId = async (
      block: Record<string, unknown>,
      cmaField: string,
    ): Promise<string | null> => {
      const subBlockId = block[cmaField] as string | null;
      if (!subBlockId) return null;
      const subBlock = await client.items.find(subBlockId);
      return getModelId(subBlock);
    };

    const result: Partial<BlockModelIds> = {};

    for (const blockId of candidateBlockIds) {
      const block = await client.items.find(blockId);
      result.configModelId ??= getModelId(block);

      const pending = SUB_CONFIG_FIELD_MAP.filter(
        ({ modelIdKey }) => !result[modelIdKey],
      );
      const resolved = await Promise.all(
        pending.map(async ({ cmaField, modelIdKey }) => ({
          modelIdKey,
          value: await resolveSubBlockModelId(block, cmaField),
        })),
      );
      for (const { modelIdKey, value } of resolved) {
        if (value) result[modelIdKey] ??= value;
      }

      if (SUB_CONFIG_FIELD_MAP.every(({ modelIdKey }) => result[modelIdKey])) {
        break;
      }
    }

    // Keys typed as `string` (not `string | null`) in BlockModelIds are required.
    const requiredKeys = Object.keys(
      REQUIRED_MODEL_ID_KEYS,
    ) as RequiredModelIdKey[];
    for (const key of requiredKeys) {
      if (!result[key]) {
        throw new Error(
          `No configuration block has a ${key} — cannot discover block model IDs.`,
        );
      }
    }

    return result as BlockModelIds;
  }

  /** Clear cache - useful when templates are known to have changed */
  clearCache(): void {
    this.cache.clear();
    this.logger.debug('Video template cache cleared');
  }
}

/**
 * Maps AspectRatio to the DatoCMS CMA API field key (snake_case).
 */
/**
 * Converts logo config from camelCase app format to snake_case CMA format,
 * and transforms the `logo` file reference to DatoCMS `upload_id` format.
 */
function formatLogoConfigForCma(
  logoConfig: Record<string, unknown>,
): Record<string, unknown> {
  const snakeCase = transformKeysToSnakeCase(logoConfig);

  // DatoCMS file fields expect { upload_id: "..." }, not { id: "...", url: "..." }
  if (
    snakeCase.logo &&
    typeof snakeCase.logo === 'object' &&
    'id' in snakeCase.logo
  ) {
    snakeCase.logo = { upload_id: (snakeCase.logo as { id: string }).id };
  }

  // DatoCMS placement/size fields are integer type — round floats
  const integerFields = [
    'placement_x',
    'placement_y',
    'width',
    'height',
    'opacity',
  ];
  for (const field of integerFields) {
    if (typeof snakeCase[field] === 'number') {
      snakeCase[field] = Math.round(snakeCase[field] as number);
    }
  }

  return snakeCase;
}

function formatCaptionsConfigForCma(
  captionsConfig: Record<string, unknown>,
): Record<string, unknown> {
  const snakeCase = transformKeysToSnakeCase(captionsConfig);

  // DatoCMS position fields are integer type — round floats
  const integerFields = [
    'vertical_position',
    'horizontal_position',
    'font_size',
  ];
  for (const field of integerFields) {
    if (typeof snakeCase[field] === 'number') {
      snakeCase[field] = Math.round(snakeCase[field] as number);
    }
  }

  return snakeCase;
}

function formatNamecardConfigForCma(
  namecardConfig: Record<string, unknown>,
): Record<string, unknown> {
  const snakeCase = transformKeysToSnakeCase(namecardConfig);

  // placement_override is a JSON field — DatoCMS CMA expects a JSON string,
  // not a native object. Keep inner keys in camelCase to match how the app
  // reads them back via GraphQL.
  if (namecardConfig.placementOverride) {
    snakeCase.placement_override = JSON.stringify(
      namecardConfig.placementOverride,
    );
  }

  return snakeCase;
}

/**
 * PLAT-11034 — the hook carries BOTH a composition link and a JSON placement
 * override, so it needs the card formatter's link handling and the namecard
 * formatter's JSON stringification.
 */
function formatHookConfigForCma(
  hookConfig: Record<string, unknown>,
): Record<string, unknown> {
  const snakeCase = formatCardConfigForCma(hookConfig);

  // placement_override is a JSON field — the CMA wants a string, and the inner
  // keys stay camelCase to match how the app reads them back via GraphQL.
  if (hookConfig.placementOverride) {
    snakeCase.placement_override = JSON.stringify(hookConfig.placementOverride);
  }

  return snakeCase;
}

function formatCardConfigForCma(
  cardConfig: Record<string, unknown>,
): Record<string, unknown> {
  const snakeCase = transformKeysToSnakeCase(cardConfig);

  // composition_template is a link field — pass the ID string
  if (
    snakeCase.composition_template &&
    typeof snakeCase.composition_template === 'object' &&
    'id' in (snakeCase.composition_template as Record<string, unknown>)
  ) {
    snakeCase.composition_template = (
      snakeCase.composition_template as { id: string }
    ).id;
  }

  return snakeCase;
}

const OVERRIDE_CMA_KEYS = [
  'override16x9',
  'override9x16',
  'override1x1',
] as const;

/** Maps sub-config CMA field names to their block model ID keys. */
const SUB_CONFIG_FIELD_MAP = [
  { cmaField: 'logo_configuration', modelIdKey: 'logoConfigModelId' },
  { cmaField: 'captions_configuration', modelIdKey: 'captionsConfigModelId' },
  { cmaField: 'namecard_configuration', modelIdKey: 'namecardConfigModelId' },
  {
    cmaField: 'intro_card_configuration',
    modelIdKey: 'introCardConfigModelId',
  },
  { cmaField: 'end_card_configuration', modelIdKey: 'endCardConfigModelId' },
  { cmaField: 'hook_configuration', modelIdKey: 'hookConfigModelId' },
] as const;

function getOverrideCmaFieldKey(aspectRatio: AspectRatio): string {
  switch (aspectRatio) {
    case '16:9':
      return 'override16x9';
    case '9:16':
      return 'override9x16';
    case '1:1':
      return 'override1x1';
    default:
      return 'override16x9';
  }
}
